import { FormProvider, useForm } from "react-hook-form";
import PatientIdentifierInput, {
  type PatientIdentifierInputFields,
} from "@/forms/RegisterPatientForm/PatientIdentifierInput";
import Form from "@/components/Form/Form";
import { Trans } from "@lingui/react/macro";
import { useLingui } from "@lingui/react/macro";
import { FilledButton } from "@medoma/components/Button/Button";
import { useNavigate } from "react-router";
import { generateQueryString } from "@/api/Helpers";
import { fetchCareEpisodes, normalizePatientIdentifier } from "@/api/Patients";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { deducedError } from "@/Utils/ErrorUtils";
import { Spinner } from "@medoma/components/Spinner/Spinner";
import { useUserProfile } from "@/api/Users";
import { primaryPatientIdentifierType } from "@medoma/models/organizations";
import {
  personalIdentityNumberTypeSchema,
  patientStatusSchema,
} from "@medoma/models/patients";
import { useSelectedSite } from "@/Utils/useSelectedSite";

const PatientIdentifierCheck = () => {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { organization } = useUserProfile();
  const selectedSite = useSelectedSite();
  const [isFetchingCareEpisode, setIsFetchingCareEpisode] = useState(false);
  const [fetchCareEpisodeError, setFetchCareEpisodeError] = useState<
    string | null
  >(null);
  const methods = useForm<PatientIdentifierInputFields>({
    defaultValues: {
      identifier: "",
      isTemporaryNumber: false,
    },
  });
  const { handleSubmit } = methods;

  const primaryIdentifierType = primaryPatientIdentifierType(organization);

  const validateAndSubmit = handleSubmit(async (formData) => {
    const { identifier: rawIdentifier, isTemporaryNumber } = formData;
    const identifier = normalizePatientIdentifier({
      identifier: rawIdentifier,
      identifierType: isTemporaryNumber
        ? personalIdentityNumberTypeSchema.enum.SwedishTemporaryIdentityNumber
        : primaryIdentifierType,
    });
    const limit = 1;
    const sort = "desc" as const;

    let queryParams;
    if (isTemporaryNumber) {
      queryParams = {
        limit,
        sort,
        "temporary-number": identifier,
      };
    } else {
      switch (primaryIdentifierType) {
        case personalIdentityNumberTypeSchema.enum
          .SwedishPersonalIdentityNumber:
          queryParams = {
            limit,
            sort,
            "swedish-identity-number": identifier,
          };
          break;
        case personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber:
          queryParams = {
            limit,
            sort,
            "swiss-emedicus-number": identifier,
          };
          break;
        case personalIdentityNumberTypeSchema.enum.SwissPatientNumber:
          queryParams = {
            limit,
            sort,
            "swiss-patient-number": identifier,
          };
          break;
        case personalIdentityNumberTypeSchema.enum.LatvianPersonasKods:
          queryParams = {
            limit,
            sort,
            "latvian-personas-kods": identifier,
          };
          break;
        default:
          throw primaryIdentifierType satisfies never;
      }
    }

    if (fetchCareEpisodeError !== null) {
      setFetchCareEpisodeError(null);
    }

    setIsFetchingCareEpisode(true);

    try {
      const careEpisodes = await fetchCareEpisodes({
        queryParams,
      });
      setIsFetchingCareEpisode(false);

      if (careEpisodes.length === 0) {
        let prospectQueryParams;
        if (isTemporaryNumber) {
          prospectQueryParams = { "temporary-number": identifier };
        } else {
          switch (primaryIdentifierType) {
            case personalIdentityNumberTypeSchema.enum
              .SwedishPersonalIdentityNumber:
              prospectQueryParams = { "swedish-identity-number": identifier };
              break;
            case personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber:
              prospectQueryParams = { "swiss-emedicus-number": identifier };
              break;
            case personalIdentityNumberTypeSchema.enum.SwissPatientNumber:
              prospectQueryParams = { "swiss-patient-number": identifier };
              break;
            case personalIdentityNumberTypeSchema.enum.LatvianPersonasKods:
              prospectQueryParams = { "latvian-personas-kods": identifier };
              break;
            default:
              throw primaryIdentifierType satisfies never;
          }
        }

        void navigate(
          `../../new-prospect${generateQueryString(prospectQueryParams)}`,
        );
        return;
      }

      const latestCareEpisodeOnCurrentSite = careEpisodes.find(
        (careEpisode) => careEpisode.site.id === selectedSite,
      );
      const hasActiveCareEpisodeOnCurrentSite =
        latestCareEpisodeOnCurrentSite?.status ===
          patientStatusSchema.enum.prospect ||
        latestCareEpisodeOnCurrentSite?.status ===
          patientStatusSchema.enum.admitted;

      if (hasActiveCareEpisodeOnCurrentSite) {
        setFetchCareEpisodeError(
          t`En patient med identifierare ${rawIdentifier} är redan aktiv.`,
        );
        return;
      }

      // We know from the `careEpisodes.length === 0`-check above that at least one careEpisode exists for this identifier
      const latestCareEpisode = careEpisodes[0]!;

      // Put the latest care episode in the cache
      queryClient.setQueryData(
        ["patientLatestCareEpisode", identifier, queryParams],
        latestCareEpisode,
      );

      let returningQueryParams;
      if (isTemporaryNumber) {
        returningQueryParams = { "temporary-number": identifier };
      } else {
        switch (primaryIdentifierType) {
          case personalIdentityNumberTypeSchema.enum
            .SwedishPersonalIdentityNumber:
            returningQueryParams = { "swedish-identity-number": identifier };
            break;
          case personalIdentityNumberTypeSchema.enum.SwissEMedicusNumber:
            returningQueryParams = { "swiss-emedicus-number": identifier };
            break;
          case personalIdentityNumberTypeSchema.enum.SwissPatientNumber:
            returningQueryParams = { "swiss-patient-number": identifier };
            break;
          case personalIdentityNumberTypeSchema.enum.LatvianPersonasKods:
            returningQueryParams = { "latvian-personas-kods": identifier };
            break;
          default:
            throw primaryIdentifierType satisfies never;
        }
      }

      void navigate(`../returning${generateQueryString(returningQueryParams)}`);
    } catch (error: unknown) {
      setIsFetchingCareEpisode(false);
      setFetchCareEpisodeError(deducedError(error));
    }
  });

  return (
    <FormProvider {...methods}>
      <Form onSubmit={validateAndSubmit}>
        <Form.Section>
          <PatientIdentifierInput />
        </Form.Section>
        <FilledButton type="submit" disabled={isFetchingCareEpisode}>
          {isFetchingCareEpisode ? <Spinner /> : null}
          <Trans>Fortsätt</Trans>
        </FilledButton>
      </Form>
      {fetchCareEpisodeError ? (
        <ErrorMessage message={fetchCareEpisodeError} />
      ) : null}
    </FormProvider>
  );
};

export default PatientIdentifierCheck;
